import React, { useState } from 'react'
import slugify from 'react-slugify'
import { mapMenuItems, loadSavedSectionStorage, getDisplayUserName, filterMenuItems, filterWorkspaces, getWorkspaceIconText, settingsPageChek, mapMenuSettings, toggleSection, expandAll, getOpenIds } from './utils'
import { headerLinks, settingsMainLinks } from './constants'
import { getInitials } from 'utils/common'
import { VuiCollapsibleMenu, VuiSidebarMenu, VuiTooltip } from '@topkamera/viso-ui'
import { socket } from 'socket'
import { routeMapping } from 'routes'
import { CustomSection } from './components/CustomSection'
import { getPermission } from 'utils/permissions'
import { isWorkspaceFeatureEnabled } from 'utils/workspaceFeatures'
import LeftPanel from 'containers/LeftPanel'
import SideBarMainNav from './components/SideBarMainNav'
import './style.scss'

const SideBar = ({
  detailRole, workspace = {}, notificationProps, pendingDevices, isCollapsed, updateSidebarDisplay, user, workspaces = [], router, selectWorkspaceRequest, logout, vendor, isMobile, hasBanner, isLoading, fetchWorkspaceDetailRequest
}) => {
  const { notifications, updateNotificationDisplay } = notificationProps
  const menuItems = mapMenuItems({ detailRole, workspace, pendingDevices })
  const menuSettings = mapMenuSettings({ detailRole, workspace, vendor })
  const isSettingPage = settingsPageChek(router)
  const customSections = workspace?.sideBarCustomSections || []
  const selectedMenuItems = isSettingPage ? menuSettings : menuItems
  const filterHeaderLinks = filterMenuItems(headerLinks, detailRole, workspace)
  const [closeMenuIds, setCloseMenuIds] = useState(loadSavedSectionStorage('sidebarClosedMenu'))
  const [closeSettingIds, setCloseSettingIds] = useState(loadSavedSectionStorage('sidebarClosedSettings'))
  const [closeCustomIds, setCloseCustomIds] = useState(loadSavedSectionStorage('sidebarClosedCustom'))
  const [selectedWs, setSelectedWs] = useState({})
  const [wsData, setWsData] = useState({})
  const [showWarning, setShowWarning] = useState(false)
  const [showVerificationCode, setShowVerificationCode] = useState(false)
  const [isSuspended, setIsSuspended] = useState(false)
  const sidebarPermission = getPermission('permissionSideBarCustomSections', user.role)
  const sidebarCustomEnabled = isWorkspaceFeatureEnabled(workspace, 'sideBarCustom');

  const onSelectWorkspace = (data) => {
    const args = { subDomain: data }
    const workspace = workspaces.find((item) => item.subDomain === data)

    if (workspace) {
      if (workspace.status === 'inactive') {
        setSelectedWs(workspace)
        return setShowWarning(true)
      }

      if (workspace.status === 'inactive' && workspace.userRole === 'Owner') {
        setSelectedWs(workspace)
        return setIsSuspended(true)
      }

      if (workspace.userRole === 'Owner' || workspace.status === 'active') {
        if (workspace.twoFactorEnabled) {
          setShowVerificationCode(true)
          return setWsData(args)
        }

        socket.close()
        return selectWorkspaceRequest(args, { push: router.navigate })
      }
    }

    setSelectedWs(workspace)
    setShowWarning(true)
  }

  const redirectToUserPage = () => {
    user && router.navigate(`/${workspace && slugify(workspace.name)}/settings/profile`)
  }

  const onLogout = () => {
    logout()
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("userData")
    localForage.setItem("activePartnerId", null)
    localForage.setItem("hideMaintenanceBanner", false)
    setTimeout(() => router.navigate(routeMapping.LOGIN.path), 100)
  }

  return (
    <div className='viso-sidebar'>
      <LeftPanel
        isLoading={isLoading}
        hasBanner={hasBanner}
        selectedWs={selectedWs}
        wsData={wsData}
        showWarning={showWarning}
        showVerificationCode={showVerificationCode}
        setShowVerificationCode={setShowVerificationCode}
        isSuspended={isSuspended}
      />
      {!isMobile &&
        <VuiSidebarMenu
          titleBold='viso'
          titleMedium='suite'
          isCollapsed={isCollapsed}
          onToggleCollapse={() => {
            const newState = !isCollapsed
            updateSidebarDisplay(newState)
            localStorage.setItem('sidebar-collapsed', newState.toString())
          }}
          onToggleAll={(value) => expandAll({
            value,
            menu: selectedMenuItems,
            storageKey: isSettingPage ? 'sidebarClosedSettings' : 'sidebarClosedMenu',
            callback: isSettingPage ? setCloseSettingIds : setCloseMenuIds,
            customParams: {
              customSections: customSections,
              customKey: 'sidebarClosedCustom',
              customCallback: setCloseCustomIds
            }
          })}
          customMenu={
            <>
              {isSettingPage ?
                <div className='vui-pb-15'>
                  <VuiCollapsibleMenu {...settingsMainLinks} />
                </div>
                :
                <div className='vui-gap-15 vui-flex vui-flex-column'>
                  <SideBarMainNav
                    unreadCount={notifications?.unreadCount}
                    updateNotificationDisplay={updateNotificationDisplay}
                    workspace={workspace}
                  />
                  {sidebarPermission.includes('read') && sidebarCustomEnabled &&
                    <CustomSection
                      sideBarCustomSections={customSections}
                      onSuccess={fetchWorkspaceDetailRequest}
                      router={router}
                      workspace={workspace}
                      detailRole={detailRole}
                      onHeaderClick={toggleSection}
                      setCloseCustomIds={setCloseCustomIds}
                      closeCustomIds={closeCustomIds}
                      isCollapsed={isCollapsed}
                      permission={sidebarPermission}
                    />
                  }
                </div>
              }
            </>
          }
          profile={{
            header: {
              items: [
                {
                  captions: user ? [getDisplayUserName(user)] : [],
                  image: workspace.logo,
                  iconText: !workspace.logo && getWorkspaceIconText(workspace.name || workspace.organizationName),
                  isLight: !workspace.logo,
                  style: 'border',
                  text: workspace.name
                }
              ]
            },
            menu: [
              ...filterHeaderLinks,
              {
                hasPermissions: true,
                type: 'dropdown',
                label: 'Switch Workspace',
                elementId: 'subMenu',
                disabled: workspaces.length <= 1,
                menu: filterWorkspaces(workspaces, workspace, onSelectWorkspace)
              },
              {
                hasPermissions: true,
                type: 'divider',
              },
              {
                hasPermissions: true,
                type: 'rowicon',
                iconText: getInitials(user.firstName, user.lastName),
                label: getDisplayUserName(user) || '-',
                style: 'border',
                caption: user.email,
                onClick: redirectToUserPage
              },
              {
                hasPermissions: true,
                type: 'button',
                label: 'User Settings',
                type: 'button',
                to: `/${workspace && slugify(workspace.name)}${routeMapping.PROFILE.path}`
              },
              {
                hasPermissions: true,
                type: 'button',
                label: 'Sign out',
                onClick: onLogout
              }
            ],
            elementId: 'menu'
          }}
          menu={{
            openIds: getOpenIds({
              items: selectedMenuItems,
              ids: isSettingPage ? closeSettingIds : closeMenuIds,
            }),
            dataTooltipId: 'customTooltipPortal',
            onHeaderClick: (id) => toggleSection({
              id,
              menuIds: isSettingPage ? closeSettingIds : closeMenuIds,
              storageKey: isSettingPage ? 'sidebarClosedSettings' : 'sidebarClosedMenu',
              callback: isSettingPage ? setCloseSettingIds : setCloseMenuIds
            }),
            items: selectedMenuItems
          }}
        />
      }
      <div id='menu' className='viso-sidebar__portal' />
      <div id='subMenu' className='viso-sidebar__portal' />
      <VuiTooltip tooltipId='customTooltipPortal' position='right' offset={-5} />
    </div>
  )
}

export default SideBar